/**
 * 🔒 Conversation View (piece 32; decision 1228, T400): a per-person switch on
 * the mailbox toolbar that draws the list as one row per thread. A thread is
 * read by its root, `threadRootMessageId?` or the message's own id, and never by
 * its subject (decision 0082), so two conversations sharing a subject stay two.
 */
import { MAIL_API_PATH } from './client';
import { fill, t } from '../strings';

/** Person scope, true or false, with no Organization default: an absent row is off. */
export const MAIL_CONVERSATION_VIEW_KEY = 'mail.conversationView';
/** The toolbar's switch writes the requester's own row here, and nobody else's. */
export const MAIL_CONVERSATION_VIEW_PATH = `${MAIL_API_PATH}/settings/conversation-view`;

/** The stored row read as the switch: only a stored true is on. */
export function conversationViewFrom(stored: unknown): boolean {
  return stored === true;
}

/** The thread a message belongs to: its root's id, or its own where it is a root. */
export function threadRootOf(message: { id: string; threadRootMessageId: string | null }): string {
  return message.threadRootMessageId ?? message.id;
}

/**
 * The newest message of each thread among rows already ordered newest first,
 * one per root, in the order the list draws them. A later row of a thread
 * already drawn is folded into it.
 */
export function newestPerThread<R extends { id: string }>(rows: readonly R[], rootOf: (row: R) => string): R[] {
  const seen = new Set<string>();
  const out: R[] = [];
  for (const row of rows) {
    const root = rootOf(row);
    if (seen.has(root)) continue;
    seen.add(root);
    out.push(row);
  }
  return out;
}

/** 🔒 A thread's participants: the senders in the order they first wrote, three named and the rest counted. */
export function participantsText(senders: readonly string[], named = 3): string {
  const distinct: string[] = [];
  for (const sender of senders) if (sender && !distinct.includes(sender)) distinct.push(sender);
  if (distinct.length <= named) return distinct.join(', ');
  return `${distinct.slice(0, named).join(', ')} ${fill(t('mail.conversation.andMore'), { n: String(distinct.length - named) })}`;
}
